import { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Col, Row, Form, Button } from 'react-bootstrap';
import { format, addMonths } from 'date-fns';
import ReactSelect from 'react-select';

import Flex from 'components/common/Flex';
import CreateSponsorNav from './CreateSponsorNav';
import IconButton from 'components/common/IconButton';
import LoadingButton from 'components/common/LoadingButton';
import SelectBeneficiaryModal from './SelectBeneficiaryModal';
import SelectDonationModal from './SelectDonationModal';
import FalconCardHeader from 'components/common/FalconCardHeader';
import SoftBadge from 'components/common/SoftBadge';
import Avatar from 'components/common/Avatar';
import LoadingContainer from 'components/common/LoadingContainer';
import { useDonorsWithRemainingDonations } from 'hooks/useBeneficiaries';
import { formatMoney } from 'helpers/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const SponsorshipForm = props => {
  const { onSubmit, loading, beneficiary, beneficiaryLoading } = props;

  const [isSelectBeneficiaryModalOpen, setIsSelectBeneficiaryModalOpen] = useState(false);
  const [isSelectDonationModalOpen, setIsSelectDonationModalOpen] = useState(false);
  const [sponsorshipItems, setSponsorshipItems] = useState([]);

  // Form state for adding new item
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [coveragePercent, setCoveragePercent] = useState(100);
  const [selectedDuration, setSelectedDuration] = useState(null); // null = max duration
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const beneficiaryId = searchParams.get('beneficiaryId') || searchParams.get('id');

  const { donorsWithDonations, isLoading: loadingDonors } =
    useDonorsWithRemainingDonations({ includeAll: true });

  // Get beneficiary's sponsorship types
  const sponsorshipTypes = beneficiary?.sponsorship_types || [];

  // Virtual remaining amounts (account for items already added in this session).
  // Kept unfiltered so the picker can show every donation; "fully allocated"
  // rows are flagged inside the modal and disabled from selection there.
  const donationsWithVirtualBalance = useMemo(() => {
    return donorsWithDonations.map(donation => {
      const alreadyAllocated = sponsorshipItems
        .filter(item => item.orderDetailId === donation.orderDetailId)
        .reduce((sum, item) => sum + item.allocatedAmount, 0);

      return {
        ...donation,
        virtualRemainingAmount: donation.remainingAmount - alreadyAllocated
      };
    });
  }, [donorsWithDonations, sponsorshipItems]);

  // Sponsorship type options for ReactSelect
  const typeOptions = sponsorshipTypes.map(type => ({
    value: type.typeId,
    label: `${type.typeName} - ${formatMoney(type.cost)}/month`,
    typeInfo: type
  }));

  // When a donation is picked from the modal, wrap it in the existing
  // {value, label, donation} shape so downstream coverage logic is unchanged.
  const handleDonationSelect = donation => {
    const virtual = donationsWithVirtualBalance.find(
      d => d.orderDetailId === donation.orderDetailId
    );
    const remainingForCalc =
      virtual?.virtualRemainingAmount ?? donation.remainingAmount;
    setSelectedDonation({
      value: donation.orderDetailId,
      label: `${donation.donor?.first_name || ''} ${donation.donor?.last_name || ''} - ${formatMoney(remainingForCalc)} remaining (${donation.donationType || 'General'})`,
      donation: {
        ...donation,
        remainingAmount: remainingForCalc
      }
    });
    setSelectedDuration(null);
  };

  // Prefill the donation (and sponsorship type) when arriving from the
  // Sponsorship Donation "Assign" flow, which passes ?orderDetailId=... so the
  // donation-first worklist lands the user on a ready-to-go form instead of a
  // blank one. Runs once, after donations and the beneficiary have loaded.
  const orderDetailIdParam = searchParams.get('orderDetailId');
  const prefilledDonationRef = useRef(false);
  useEffect(() => {
    if (prefilledDonationRef.current) return;
    if (!orderDetailIdParam || loadingDonors || beneficiaryLoading) return;
    prefilledDonationRef.current = true;

    const donation = donorsWithDonations.find(
      d => d.orderDetailId === orderDetailIdParam
    );
    if (!donation) return;

    handleDonationSelect(donation);

    // Preselect the beneficiary's sponsorship type: if there is only one, use
    // it; otherwise match the donation's package to a type by name.
    const typeMatch =
      sponsorshipTypes.length === 1
        ? sponsorshipTypes[0]
        : sponsorshipTypes.find(t =>
            donation.sponsorshipType
              ?.toLowerCase()
              .includes((t.typeName || '').toLowerCase())
          );
    if (typeMatch) {
      setSelectedType({
        value: typeMatch.typeId,
        label: `${typeMatch.typeName} - ${formatMoney(typeMatch.cost)}/month`,
        typeInfo: typeMatch
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderDetailIdParam,
    loadingDonors,
    beneficiaryLoading,
    donorsWithDonations,
    sponsorshipTypes
  ]);

  const handleChangeBeneficiary = () => {
    setIsSelectBeneficiaryModalOpen(true);
  };

  const onSelectBeneficiary = selectedBeneficiaryId => {
    searchParams.set('beneficiaryId', selectedBeneficiaryId);
    setSearchParams(searchParams);
    // Reset items when beneficiary changes
    setSponsorshipItems([]);
  };

  // Get existing coverage for selected sponsorship type
  // Checks ALL periods and finds the first one with < 100% coverage (gap)
  // Only allows extension if ALL periods are fully covered
  const getExistingCoverage = () => {
    if (!selectedType) return { totalCoverage: 0, existingDuration: null, donors: [], latestEndDate: null, startDate: null, hasGap: false };

    const itemsForType = sponsorshipItems.filter(
      item =>
        item.beneficiarySponsorshipTypeId === selectedType.typeInfo?.junctionId
    );

    if (itemsForType.length === 0) {
      return { totalCoverage: 0, existingDuration: null, donors: [], latestEndDate: null, startDate: null, hasGap: false };
    }

    // Group by start date to find periods
    const periods = itemsForType.reduce((acc, item) => {
      if (!acc[item.startDate]) acc[item.startDate] = [];
      acc[item.startDate].push(item);
      return acc;
    }, {});

    // Sort periods by start date (earliest first)
    const sortedPeriodKeys = Object.keys(periods).sort((a, b) =>
      new Date(a) - new Date(b)
    );

    // Check each period from earliest to latest to find any gaps
    let periodWithGap = null;
    let latestFullPeriod = null;

    for (const periodKey of sortedPeriodKeys) {
      const periodItems = periods[periodKey];
      const periodCoverage = periodItems.reduce((sum, item) => sum + item.coveragePercentage, 0);

      if (periodCoverage < 100) {
        // Found a gap - this is the period we should fill
        periodWithGap = {
          items: periodItems,
          coverage: periodCoverage,
          startDate: periodItems[0].startDate,
          endDate: periodItems[0].endDate,
          duration: periodItems[0].duration
        };
        break; // Stop at first gap
      } else {
        // This period is fully covered
        latestFullPeriod = {
          items: periodItems,
          coverage: periodCoverage,
          startDate: periodItems[0].startDate,
          endDate: periodItems[0].endDate,
          duration: periodItems[0].duration
        };
      }
    }

    // If there's a gap, return that period's info
    if (periodWithGap) {
      const donors = periodWithGap.items.map(item => ({
        name: item.donorName,
        coverage: item.coveragePercentage
      }));

      return {
        totalCoverage: periodWithGap.coverage,
        existingDuration: periodWithGap.duration,
        donors,
        latestEndDate: periodWithGap.endDate,
        startDate: periodWithGap.startDate,
        hasGap: true
      };
    }

    // No gaps - return the latest full period (for extension)
    if (latestFullPeriod) {
      const donors = latestFullPeriod.items.map(item => ({
        name: item.donorName,
        coverage: item.coveragePercentage
      }));

      return {
        totalCoverage: latestFullPeriod.coverage,
        existingDuration: latestFullPeriod.duration,
        donors,
        latestEndDate: latestFullPeriod.endDate,
        startDate: latestFullPeriod.startDate,
        hasGap: false
      };
    }

    return { totalCoverage: 0, existingDuration: null, donors: [], latestEndDate: null, startDate: null, hasGap: false };
  };

  const existingCoverage = getExistingCoverage();

  // Check if current period is fully covered - if so, we're extending to a new period
  const isFullyCovered = existingCoverage.totalCoverage >= 100;
  const isExtendingPeriod = isFullyCovered && existingCoverage.latestEndDate;

  // For extension: coverage resets to 100%, for sharing: limit to remaining
  const remainingCoveragePercent = isExtendingPeriod ? 100 : Math.max(0, 100 - existingCoverage.totalCoverage);

  // Calculate preview for current selection
  const getPreview = () => {
    if (!selectedType || !selectedDonation) return null;

    const typeInfo = selectedType.typeInfo;
    if (!typeInfo) return null;

    // If type already has coverage and not extending, limit to remaining percentage
    const effectiveCoveragePercent = Math.min(coveragePercent, remainingCoveragePercent);
    if (effectiveCoveragePercent <= 0) return null;

    const monthlyCost = parseFloat(typeInfo.cost) || 0;
    const monthlyAmount = (monthlyCost * effectiveCoveragePercent) / 100;

    if (monthlyAmount <= 0) return null;

    const remainingAmount = selectedDonation.donation.remainingAmount;

    // Determine the effective start date
    // For extension: start from end of previous period
    // For sharing or new: use the selected start date
    const effectiveStartDate = isExtendingPeriod
      ? existingCoverage.latestEndDate
      : (existingCoverage.startDate || startDate);

    // While the user is typing in the Start Date field the value can be empty
    // or incomplete for a moment. new Date('') is an Invalid Date, and
    // date-fns format() THROWS on it, which crashed the whole app to a blank
    // screen mid-typing. No preview until the date is valid.
    const parsedStartDate = new Date(effectiveStartDate);
    if (isNaN(parsedStartDate.getTime())) return null;

    let maxDuration, duration, insufficientForPeriod = false, requiredAmount = 0;
    if (isExtendingPeriod) {
      // Extension mode: duration is free to select (not locked)
      maxDuration = Math.floor(remainingAmount / monthlyAmount);
      duration = selectedDuration !== null ? Math.min(selectedDuration, maxDuration) : maxDuration;
    } else if (existingCoverage.existingDuration) {
      // Sharing mode: must match existing period duration
      maxDuration = Math.floor(remainingAmount / monthlyAmount);
      const requiredDuration = existingCoverage.existingDuration;

      // Check if donor has enough funds for the full period
      if (maxDuration < requiredDuration) {
        insufficientForPeriod = true;
        requiredAmount = monthlyAmount * requiredDuration;
        duration = maxDuration; // Show what they can afford
      } else {
        duration = requiredDuration; // Lock to period duration
      }
    } else {
      // New sponsorship: duration is free
      maxDuration = Math.floor(remainingAmount / monthlyAmount);
      duration = selectedDuration !== null ? Math.min(selectedDuration, maxDuration) : maxDuration;
    }

    const allocatedAmount = monthlyAmount * duration;
    const endDate = addMonths(parsedStartDate, insufficientForPeriod ? existingCoverage.existingDuration : duration);

    // For extension, coverage starts fresh; for sharing, add to existing
    const newTotalCoverage = isExtendingPeriod
      ? effectiveCoveragePercent
      : existingCoverage.totalCoverage + effectiveCoveragePercent;

    return {
      monthlyAmount,
      maxDuration,
      duration: insufficientForPeriod ? existingCoverage.existingDuration : duration,
      actualAffordableDuration: duration,
      allocatedAmount: insufficientForPeriod ? remainingAmount : allocatedAmount, // Use all remaining if insufficient
      startDate: effectiveStartDate,
      endDate,
      remainingAfter: insufficientForPeriod ? 0 : (remainingAmount - allocatedAmount),
      effectiveCoveragePercent,
      newTotalCoverage,
      isSharedSponsorship: existingCoverage.totalCoverage > 0 && !isExtendingPeriod,
      isExtension: isExtendingPeriod,
      isFillingGap: existingCoverage.hasGap,
      insufficientForPeriod,
      requiredAmount
    };
  };

  const preview = getPreview();

  // Generate duration options (1 to max months)
  const durationOptions = preview ? Array.from({ length: preview.maxDuration }, (_, i) => ({
    value: i + 1,
    label: `${i + 1} month${i + 1 > 1 ? 's' : ''} (${formatMoney(preview.monthlyAmount * (i + 1))})`
  })) : [];

  // Add sponsorship item to list
  const handleAddItem = () => {
    if (!selectedType || !selectedDonation || !preview || preview.duration < 1) {
      alert('Please select a valid sponsorship type and donation with sufficient funds');
      return;
    }

    const typeInfo = selectedType.typeInfo;
    const donation = selectedDonation.donation;

    // Check if an item already exists with same sponsorship type and donation.
    // beneficiarySponsorshipTypeId must be the junction row id
    // (beneficiary_sponsorship_types.id) - that's what the sponsorship table's
    // FK references. typeId is the generic sponsorship_type_id, used only for
    // matching against the beneficiary's configured types in the UI.
    const existingItemIndex = sponsorshipItems.findIndex(
      item => item.beneficiarySponsorshipTypeId === typeInfo.junctionId &&
              item.orderDetailId === donation.orderDetailId
    );

    const itemData = {
      beneficiarySponsorshipTypeId: typeInfo.junctionId,
      typeName: typeInfo.typeName || 'Sponsorship',
      cost: typeInfo.cost,
      donorId: donation.donor.id,
      donorName: `${donation.donor.first_name} ${donation.donor.last_name}`,
      orderDetailId: donation.orderDetailId,
      coveragePercentage: preview.effectiveCoveragePercent,
      allocatedAmount: preview.allocatedAmount,
      startDate: new Date(preview.startDate).toISOString(), // Use preview's effective start date
      duration: preview.duration,
      monthlyAmount: preview.monthlyAmount,
      endDate: preview.endDate,
      isExtension: preview.isExtension // Track if this is an extension period
    };

    if (existingItemIndex >= 0) {
      // Update existing item
      const updatedItems = [...sponsorshipItems];
      updatedItems[existingItemIndex] = {
        ...updatedItems[existingItemIndex],
        ...itemData
      };
      setSponsorshipItems(updatedItems);
    } else {
      // Create new item
      setSponsorshipItems([...sponsorshipItems, { id: Date.now(), ...itemData }]);
    }

    // Reset form - clear donation selection so user sees updated remaining balance
    setSelectedType(null);
    setSelectedDonation(null);
    setSelectedDuration(null);
    setCoveragePercent(100);
  };

  // Remove item from list
  const handleRemoveItem = (itemId) => {
    setSponsorshipItems(sponsorshipItems.filter(item => item.id !== itemId));
  };

  // Submit all items
  const handleSubmit = () => {
    if (sponsorshipItems.length === 0) {
      alert('Please add at least one sponsorship item');
      return;
    }
    onSubmit(sponsorshipItems);
  };

  // Calculate totals
  // For Total Monthly: Group by type, find the highest coverage period for each type
  // Extensions are sequential, so we don't sum them - we take the max coverage period
  const totalMonthly = useMemo(() => {
    // Group items by sponsorship type
    const typeGroups = sponsorshipItems.reduce((acc, item) => {
      if (!acc[item.beneficiarySponsorshipTypeId]) {
        acc[item.beneficiarySponsorshipTypeId] = {
          cost: item.cost,
          periods: {}
        };
      }
      // Group by period (start date) within each type
      if (!acc[item.beneficiarySponsorshipTypeId].periods[item.startDate]) {
        acc[item.beneficiarySponsorshipTypeId].periods[item.startDate] = 0;
      }
      acc[item.beneficiarySponsorshipTypeId].periods[item.startDate] += item.coveragePercentage;
      return acc;
    }, {});

    // For each type, find the period with highest coverage and calculate monthly
    return Object.values(typeGroups).reduce((sum, type) => {
      const maxCoverage = Math.min(100, Math.max(...Object.values(type.periods)));
      return sum + (parseFloat(type.cost) * maxCoverage / 100);
    }, 0);
  }, [sponsorshipItems]);

  const totalAllocated = sponsorshipItems.reduce((sum, item) => sum + item.allocatedAmount, 0);

  if (beneficiaryLoading) {
    return <LoadingContainer />;
  }

  const beneficiaryName = beneficiary
    ? `${beneficiary.first_name} ${beneficiary.middle_name || ''} ${beneficiary.last_name}`.trim()
    : 'Select Beneficiary';

  return (
    <>
      <SelectBeneficiaryModal
        onHide={() => setIsSelectBeneficiaryModalOpen(false)}
        show={isSelectBeneficiaryModalOpen}
        onSubmit={onSelectBeneficiary}
        beneficiaryId={beneficiaryId}
      />

      <SelectDonationModal
        show={isSelectDonationModalOpen}
        onHide={() => setIsSelectDonationModalOpen(false)}
        onSubmit={handleDonationSelect}
        initialId={selectedDonation?.value}
      />

      <div className="py-0">
        <Row className="g-3">
          <Col md={4} lg={3}>
            <CreateSponsorNav onChangeBeneficiary={handleChangeBeneficiary} />
          </Col>
          <Col md={8} lg={9}>
            {/* Header Card */}
            <Card className="mb-3">
              <FalconCardHeader
                title={
                  <Flex>
                    <IconButton
                      variant="default"
                      className="shadow-none text-primary p-0"
                      iconClassName={'me-3'}
                      icon={'arrow-left'}
                      onClick={() => navigate(-1)}
                    />
                    <h4 className="mt-2 fw-bold fs-0 fs-md-2">
                      Create Sponsorship
                    </h4>
                  </Flex>
                }
                light={false}
                titleTag="div"
                menuClassName="align-items-center"
                endEl={
                  <Flex alignItems={'center'}>
                    <LoadingButton
                      transform="shrink-3"
                      className="me-2"
                      onClick={handleSubmit}
                      loading={loading}
                      disabled={sponsorshipItems.length === 0}
                    >
                      Save Sponsorship
                    </LoadingButton>
                  </Flex>
                }
              />
            </Card>

            {/* Beneficiary Info */}
            {beneficiary && (
              <Card className="mb-3">
                <Card.Body>
                  <Flex alignItems="center">
                    <Avatar size="3xl" name={beneficiaryName} className="me-3" />
                    <div>
                      <h5 className="mb-1">{beneficiaryName}</h5>
                      <p className="mb-0 text-600">ID: B{beneficiary.simple_id}</p>
                      <p className="mb-0 text-600">{beneficiary.country || 'Unknown location'}</p>
                    </div>
                  </Flex>

                  {/* Sponsorship Types */}
                  <hr />
                  <h6 className="mb-3">Sponsorship Types Needed</h6>
                  {sponsorshipTypes.length === 0 ? (
                    <p className="text-warning mb-0">
                      <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                      No sponsorship types defined for this beneficiary.
                      Please add types in the beneficiary profile first.
                    </p>
                  ) : (
                    <Row className="g-2">
                      {sponsorshipTypes.map((type, idx) => (
                        <Col key={idx} xs={12} md={6}>
                          <div className="border rounded p-2">
                            <Flex justifyContent="between" alignItems="center">
                              <span className="fw-medium">{type.typeName}</span>
                              <SoftBadge bg="primary">{formatMoney(type.cost)}/mo</SoftBadge>
                            </Flex>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Add Sponsorship Form */}
            {beneficiary && sponsorshipTypes.length > 0 && (
              <Card className="mb-3">
                <Card.Header>
                  <Card.Title className="mb-0">Add Sponsor</Card.Title>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    {/* Start Date */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={startDate}
                          onChange={e => setStartDate(e.target.value)}
                        />
                      </Form.Group>
                    </Col>

                    {/* Sponsorship Type */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Sponsorship Type</Form.Label>
                        <ReactSelect
                          value={selectedType}
                          onChange={option => {
                            setSelectedType(option);
                            setSelectedDuration(null); // Reset duration when type changes
                          }}
                          options={typeOptions}
                          placeholder="Select type..."
                          classNamePrefix="react-select"
                          isClearable
                        />
                      </Form.Group>
                    </Col>

                    {/* Select Donation */}
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Select Donation</Form.Label>
                        {loadingDonors ? (
                          <p className="text-muted">Loading donations...</p>
                        ) : donorsWithDonations.length === 0 ? (
                          <p className="text-warning">
                            <FontAwesomeIcon
                              icon="exclamation-triangle"
                              className="me-2"
                            />
                            No donations available.
                          </p>
                        ) : (
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Button
                              variant="falcon-default"
                              size="sm"
                              type="button"
                              onClick={() => setIsSelectDonationModalOpen(true)}
                            >
                              {selectedDonation ? 'Change Donation' : 'Choose Donation'}
                            </Button>
                            {selectedDonation && (
                              <>
                                <span className="fs--1 text-700">
                                  {selectedDonation.label}
                                </span>
                                <Button
                                  variant="link"
                                  size="sm"
                                  type="button"
                                  className="p-0 text-danger fs--1"
                                  onClick={() => {
                                    setSelectedDonation(null);
                                    setSelectedDuration(null);
                                  }}
                                >
                                  Clear
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* Coverage Percentage */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          Coverage Percentage: <strong>{Math.min(coveragePercent, remainingCoveragePercent)}%</strong>
                          {isExtendingPeriod ? (
                            <SoftBadge bg="success" className="ms-2">
                              <FontAwesomeIcon icon="plus-circle" className="me-1" />
                              New Period
                            </SoftBadge>
                          ) : existingCoverage.hasGap ? (
                            <SoftBadge bg="warning" className="ms-2">
                              <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
                              Filling Gap
                            </SoftBadge>
                          ) : existingCoverage.totalCoverage > 0 && (
                            <span className="text-info ms-2 fs--1">
                              ({existingCoverage.totalCoverage}% already covered)
                            </span>
                          )}
                        </Form.Label>
                        <Form.Range
                          value={Math.min(coveragePercent, remainingCoveragePercent)}
                          onChange={e => {
                            setCoveragePercent(Number(e.target.value));
                            setSelectedDuration(null);
                          }}
                          min={10}
                          max={remainingCoveragePercent}
                          step={5}
                        />
                        <small className="text-muted">
                          {isExtendingPeriod
                            ? `All periods are 100% covered. Adding new period after ${format(new Date(existingCoverage.latestEndDate), 'dd MMM yyyy')}.`
                            : existingCoverage.hasGap
                              ? `Gap found: Period ${format(new Date(existingCoverage.startDate), 'dd MMM')} - ${format(new Date(existingCoverage.latestEndDate), 'dd MMM yyyy')} needs ${remainingCoveragePercent}% more coverage.`
                              : existingCoverage.totalCoverage > 0
                              ? `Remaining coverage: ${remainingCoveragePercent}% (${existingCoverage.donors.map(d => `${d.name}: ${d.coverage}%`).join(', ')})`
                              : 'How much of the monthly cost this donor will cover.'
                          }
                        </small>
                      </Form.Group>
                    </Col>

                    {/* Duration Selector */}
                    {preview && preview.maxDuration > 0 && (
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            Duration (Months)
                            {preview.isSharedSponsorship && (
                              <SoftBadge bg="warning" className="ms-2">
                                Locked to {existingCoverage.existingDuration} months
                              </SoftBadge>
                            )}
                            {preview.isExtension && (
                              <SoftBadge bg="success" className="ms-2">
                                Extension
                              </SoftBadge>
                            )}
                          </Form.Label>
                          <ReactSelect
                            value={preview.isSharedSponsorship
                              ? durationOptions.find(o => o.value === preview.duration)
                              : (selectedDuration !== null
                                ? durationOptions.find(o => o.value === selectedDuration)
                                : durationOptions[durationOptions.length - 1])
                            }
                            onChange={option => {
                              if (!preview.isSharedSponsorship) {
                                setSelectedDuration(option?.value || null);
                              }
                            }}
                            options={preview.isSharedSponsorship
                              ? durationOptions.filter(o => o.value === preview.duration)
                              : durationOptions
                            }
                            placeholder="Select duration..."
                            classNamePrefix="react-select"
                            isDisabled={preview.isSharedSponsorship}
                          />
                          <small className="text-muted">
                            {preview.isSharedSponsorship
                              ? `Duration must match existing sponsors (${existingCoverage.existingDuration} months)`
                              : preview.isExtension
                                ? `Extending from ${format(new Date(preview.startDate), 'dd MMM yyyy')}. Max: ${preview.maxDuration} months`
                                : `Max: ${preview.maxDuration} months based on donation balance`
                            }
                          </small>
                        </Form.Group>
                      </Col>
                    )}

                    {/* Preview */}
                    {preview && (
                      <Col md={12}>
                        <Card className="bg-light">
                          <Card.Body>
                            <Flex justifyContent="between" alignItems="center" className="mb-3">
                              <h6 className="mb-0">Preview</h6>
                              {preview.isFillingGap && (
                                <SoftBadge bg="warning">
                                  <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
                                  Filling Gap
                                </SoftBadge>
                              )}
                              {preview.isSharedSponsorship && !preview.isFillingGap && (
                                <SoftBadge bg="info">
                                  <FontAwesomeIcon icon="users" className="me-1" />
                                  Shared Sponsorship
                                </SoftBadge>
                              )}
                              {preview.isExtension && (
                                <SoftBadge bg="success">
                                  <FontAwesomeIcon icon="plus-circle" className="me-1" />
                                  Extension Period
                                </SoftBadge>
                              )}
                            </Flex>
                            <Row>
                              <Col xs={6} md={2}>
                                <p className="text-600 mb-0 fs--1">Start Date</p>
                                <p className="fw-bold mb-0">{format(new Date(preview.startDate), 'dd MMM yyyy')}</p>
                              </Col>
                              <Col xs={6} md={2}>
                                <p className="text-600 mb-0 fs--1">End Date</p>
                                <p className="fw-bold mb-0">{format(preview.endDate, 'dd MMM yyyy')}</p>
                              </Col>
                              <Col xs={6} md={2}>
                                <p className="text-600 mb-0 fs--1">Duration</p>
                                <p className="fw-bold mb-0">{preview.duration} months</p>
                              </Col>
                              <Col xs={6} md={2}>
                                <p className="text-600 mb-0 fs--1">Monthly</p>
                                <p className="fw-bold mb-0">{formatMoney(preview.monthlyAmount)}</p>
                              </Col>
                              <Col xs={6} md={2}>
                                <p className="text-600 mb-0 fs--1">Total</p>
                                <p className="fw-bold mb-0">{formatMoney(preview.allocatedAmount)}</p>
                              </Col>
                              <Col xs={6} md={2}>
                                <p className="text-600 mb-0 fs--1">Coverage</p>
                                <p className="fw-bold mb-0">
                                  {preview.effectiveCoveragePercent}%
                                  {preview.isSharedSponsorship && (
                                    <span className="text-600 fs--1"> → {preview.newTotalCoverage}%</span>
                                  )}
                                </p>
                              </Col>
                            </Row>
                            {preview.isFillingGap && (
                              <p className="text-warning mt-2 mb-0">
                                <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                                Filling coverage gap: This period needs {100 - existingCoverage.totalCoverage + preview.effectiveCoveragePercent}% more to be fully covered.
                                {preview.newTotalCoverage >= 100 && ' This will complete the period!'}
                              </p>
                            )}
                            {preview.isSharedSponsorship && !preview.isFillingGap && (
                              <p className="text-info mt-2 mb-0">
                                <FontAwesomeIcon icon="info-circle" className="me-2" />
                                This donor will share the same {preview.duration} months with existing sponsors.
                                Combined coverage: {preview.newTotalCoverage}%
                              </p>
                            )}
                            {preview.isExtension && (
                              <p className="text-success mt-2 mb-0">
                                <FontAwesomeIcon icon="calendar-plus" className="me-2" />
                                All periods are fully covered. This will add {preview.duration} new months starting {format(new Date(preview.startDate), 'dd MMM yyyy')}.
                              </p>
                            )}
                            {preview.insufficientForPeriod && (
                              <p className="text-danger mt-2 mb-0">
                                <FontAwesomeIcon icon="exclamation-circle" className="me-2" />
                                <strong>Insufficient funds for this period.</strong> This donation can only cover {preview.actualAffordableDuration} months,
                                but the period requires {preview.duration} months.
                                Needs {formatMoney(preview.requiredAmount)} but only has {formatMoney(preview.allocatedAmount)} available.
                                <br />
                                <small className="text-muted">
                                  Try reducing the coverage percentage or select a donation with more funds.
                                </small>
                              </p>
                            )}
                            {!preview.insufficientForPeriod && preview.remainingAfter > 0 && (
                              <p className="text-info mt-2 mb-0">
                                <FontAwesomeIcon icon="info-circle" className="me-2" />
                                {formatMoney(preview.remainingAfter)} will remain after this allocation
                              </p>
                            )}
                            {preview.duration < 1 && (
                              <p className="text-danger mt-2 mb-0">
                                <FontAwesomeIcon icon="exclamation-circle" className="me-2" />
                                Insufficient funds for at least 1 month
                              </p>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    )}

                    {/* Add Button */}
                    <Col md={12}>
                      <Button
                        variant="primary"
                        onClick={handleAddItem}
                        disabled={!selectedType || !selectedDonation || !preview || preview.duration < 1 || preview.insufficientForPeriod}
                      >
                        <FontAwesomeIcon icon="plus" className="me-2" />
                        Add Sponsorship Item
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            {/* Added Items List */}
            <Card>
              <Card.Header className="pb-0">
                <Card.Title className="fw-medium">
                  Sponsorship Items ({sponsorshipItems.length})
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {sponsorshipItems.length === 0 ? (
                  <p className="text-center text-500 mb-0">No items added yet</p>
                ) : (
                  <>
                    {/* Group items by sponsorship type AND period (start date) */}
                    {Object.entries(
                      sponsorshipItems.reduce((groups, item) => {
                        // Group by type + start date to separate periods
                        const key = `${item.beneficiarySponsorshipTypeId}_${item.startDate}`;
                        if (!groups[key]) groups[key] = [];
                        groups[key].push(item);
                        return groups;
                      }, {})
                    ).map(([groupKey, items], groupIndex) => {
                      const isShared = items.length > 1;
                      const totalCoverage = items.reduce((sum, item) => sum + item.coveragePercentage, 0);
                      const groupTotalAllocated = items.reduce((sum, item) => sum + item.allocatedAmount, 0);
                      const periodStart = format(new Date(items[0].startDate), 'dd MMM yyyy');
                      const periodEnd = format(items[0].endDate, 'dd MMM yyyy');
                      const isExtensionPeriod = items[0].isExtension;

                      return (
                        <div
                          key={groupKey}
                          className={`${isShared ? 'border-2 border-info' : isExtensionPeriod ? 'border-2 border-success' : 'border'} rounded-3 p-3 ${groupIndex > 0 ? 'mt-3' : ''}`}
                        >
                          {/* Type Header */}
                          <Flex justifyContent="between" alignItems="center" className="mb-2">
                            <div>
                              <span className="fw-bold">{items[0].typeName}</span>
                              {isExtensionPeriod && (
                                <SoftBadge bg="success" className="ms-2">
                                  <FontAwesomeIcon icon="plus-circle" className="me-1" />
                                  Extension
                                </SoftBadge>
                              )}
                              {isShared && (
                                <SoftBadge bg="info" className="ms-2">
                                  <FontAwesomeIcon icon="users" className="me-1" />
                                  Shared ({totalCoverage}% covered)
                                </SoftBadge>
                              )}
                            </div>
                            <span className="fw-bold text-primary">{formatMoney(groupTotalAllocated)}</span>
                          </Flex>

                          {/* Period info */}
                          <p className="mb-2 fs--1 text-700">
                            <FontAwesomeIcon icon="calendar-alt" className="me-1" />
                            {periodStart} - {periodEnd} ({items[0].duration} months)
                          </p>

                          {/* Individual donor items */}
                          {items.map((item, itemIndex) => (
                            <div
                              key={item.id}
                              className={`${isShared ? 'bg-light rounded p-2' : ''} ${itemIndex > 0 ? 'mt-2' : ''}`}
                            >
                              <Flex justifyContent="between" alignItems="start">
                                <div>
                                  <p className="mb-1 text-600">
                                    Donor: <span className="text-900">{item.donorName}</span>
                                  </p>
                                  <p className="mb-0 text-600">
                                    Coverage: <span className="text-900">{item.coveragePercentage}%</span>
                                    {' '}({formatMoney(item.monthlyAmount)}/month)
                                  </p>
                                </div>
                                <div className="text-end">
                                  {!isShared && (
                                    <p className="fw-bold text-primary mb-2">
                                      {formatMoney(item.allocatedAmount)}
                                    </p>
                                  )}
                                  {isShared && (
                                    <p className="text-600 mb-2 fs--1">
                                      {formatMoney(item.allocatedAmount)}
                                    </p>
                                  )}
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="text-danger p-0"
                                    onClick={() => handleRemoveItem(item.id)}
                                  >
                                    <FontAwesomeIcon icon="trash" className="me-1" />
                                    Remove
                                  </Button>
                                </div>
                              </Flex>
                            </div>
                          ))}
                        </div>
                      );
                    })}

                    <hr />
                    <Flex justifyContent="between">
                      <div>
                        <p className="fs-0 mb-0 text-1000 fw-semi-bold">
                          Total Monthly: {formatMoney(totalMonthly)}
                        </p>
                      </div>
                      <div>
                        <p className="fs-0 mb-0 text-1000 fw-semi-bold text-end">
                          Total Allocated: {formatMoney(totalAllocated)}
                        </p>
                      </div>
                    </Flex>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

SponsorshipForm.propTypes = {
  loading: PropTypes.bool,
  onSubmit: PropTypes.func,
  beneficiary: PropTypes.object,
  beneficiaryLoading: PropTypes.bool
};

export default SponsorshipForm;
